import Modal from 'components/Modal/Modal'
import { t } from 'i18next'
import { InputWithDropdown } from 'components/InputWithDropdown/InputWithDropdown'
import { Field } from 'formik'
import { BlueSwitch } from 'views/Place/Place.styled'
import { Box, Stack, Typography } from '@mui/material'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import { StyledInput } from 'components/Input/Input.styled'
import { formatMultiLanguageField } from 'lib/utils'
import { contentLanguages } from 'lib/languages'
import { Dispatch, SetStateAction } from 'react'

const fieldTypes = [
  { value: 'boolean', label: t('boolean') },
  { value: 'string', label: t('string') },
  { value: 'number', label: t('number') },
]

const booleanOptions = [
  { value: 'false', label: t('false') },
  { value: 'true', label: t('true') },
]

type FieldValueType =
  | string
  | undefined
  | {
      options: { [lang: string]: string }
      contentLanguage: string
    }
  | boolean

type AttributeModalType = {
  modal: { right: boolean }
  handleClose: () => void
  handleBlur: (fieldName: string, value: FieldValueType) => void
  name: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  type: string | null
  facetAttribute: boolean
  booleanValue: string | null
  stringValue: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  numberValue: string
  setFacetAttribute: Dispatch<SetStateAction<boolean>>
  setStringValue: Dispatch<
    SetStateAction<{
      options: { [lang: string]: string }
      contentLanguage: string
    }>
  >
  setNumberValue: Dispatch<SetStateAction<string>>
  setBooleanValue: Dispatch<SetStateAction<string | null>>
  setType: Dispatch<SetStateAction<string | null>>
  setName: Dispatch<
    SetStateAction<{
      options: { [lang: string]: string }
      contentLanguage: string
    }>
  >
  title: string
}

export const AttributeModal = ({
  modal,
  handleClose,
  handleBlur,
  name,
  type,
  facetAttribute,
  booleanValue,
  stringValue,
  numberValue,
  setFacetAttribute,
  setStringValue,
  setNumberValue,
  setBooleanValue,
  setType,
  setName,
  title,
}: AttributeModalType) => {
  const clearValueStates = () => {
    setStringValue({
      options: formatMultiLanguageField(undefined),
      contentLanguage: contentLanguages[0],
    })
    setBooleanValue(null)
    setNumberValue('')
  }

  const handleSelectType = (value: string) => {
    clearValueStates()
    handleBlur('productAttributeValueBoolean', undefined)
    setType(value)
  }

  return (
    <Modal
      showButton={false}
      setModal={handleClose}
      modal={modal}
      title={title}>
      <Stack spacing={3}>
        <InputWithDropdown
          label={t('name')}
          value={name.options[name.contentLanguage]}
          selectedOption={name.contentLanguage}
          dropdownFieldName=''
          inputFieldName=''
          inputOptions={name.options}
          handleChange={(options) => {
            setName({ options, contentLanguage: name.contentLanguage })
          }}
          handleLanguageChange={(option) => {
            setName({ options: name.options, contentLanguage: option })
          }}
          handleBlur={() => handleBlur('productAttributeName', name)}
        />
        <SelectDropdown
          marginBottom='0px'
          selectOptions={fieldTypes}
          label={t('type')}
          selectedValue={type ?? ''}
          handleChange={(value) => {
            handleSelectType(value)
          }}
        />
        {type === 'boolean' && (
          <SelectDropdown
            marginBottom='0px'
            selectOptions={booleanOptions}
            label={t('value')}
            selectedValue={booleanValue ?? ''}
            handleChange={(value) => setBooleanValue(value)}
            handleBlur={() => {
              const value = booleanValue === 'true'
              handleBlur('productAttributeValueBoolean', value)
            }}
          />
        )}
        {type === 'number' && (
          <StyledInput
            error={Number.isNaN(Number(numberValue))}
            helperText={
              Number.isNaN(Number(numberValue)) ? t('mustBeNumeric') : null
            }
            label={t('value')}
            width='100%'
            variant='filled'
            value={numberValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNumberValue(event.target.value)
            }
            onBlur={() => {
              if (!Number.isNaN(Number(numberValue))) {
                handleBlur('productAttributeValueNumeric', numberValue)
              }
            }}
          />
        )}
        {type === 'string' && (
          <InputWithDropdown
            label={t('value')}
            value={stringValue.options[stringValue.contentLanguage]}
            selectedOption={stringValue.contentLanguage}
            dropdownFieldName=''
            inputFieldName=''
            inputOptions={stringValue.options}
            handleChange={(options) => {
              setStringValue({
                options,
                contentLanguage: stringValue.contentLanguage,
              })
            }}
            handleLanguageChange={(option) => {
              setStringValue({
                options: stringValue.options,
                contentLanguage: option,
              })
            }}
            handleBlur={() =>
              handleBlur('productAttributeValueString', stringValue)
            }
          />
        )}
        <Box display='flex' alignItems='center'>
          <Field
            component={BlueSwitch}
            label={t('facetAttribute')}
            value={facetAttribute}
            checked={facetAttribute}
            onChange={() => setFacetAttribute(!facetAttribute)}
            aria-label={t('toggleFacetAttribute')}
            onBlur={() => handleBlur('isFacetAttribute', facetAttribute)}
          />
          <Typography variant='body2' ml={1}>
            {t('facetAttribute')}
          </Typography>
        </Box>
      </Stack>
    </Modal>
  )
}
